import { ActionList, Badge, BlockStack, Box, Button, ButtonGroup, InlineStack, Popover, Select, Text, TextField, Tooltip } from '@shopify/polaris';
import { MenuHorizontalIcon, PlusIcon } from '@shopify/polaris-icons';
import { useMemo, useState } from 'react';
import { useLookupStore } from '../../../context/useStore';
import ProjectSelect from '../projectSelect';
import { formatToCurrency } from '../functions';
import { labelFor, toneFor } from './approvalStatus';

/*
  The cost split of a document: one row per approval line (amount, cost type, project, business unit, note).
  Compact when the document has a single editable line (the common case): amount is fixed to the document total
  and only cost type / project / business unit are asked. "Split" expands the grid.
  Rows of other people or already decided rows are shown read-only.
*/
const GRID_COLUMNS = (hasCostType) => `130px ${hasCostType ? '1fr' : ''} 1.6fr 120px 1fr 110px 40px`;

export default function AllocationGrid({ draft, docType = 'invoice', canEdit = true, onHandOff, purchaseOrders = [], prefill = null, compactAllowed = true }) {
  const purchasetypes = useLookupStore((state) => state.purchasetypes);
  const businessunits = useLookupStore((state) => state.businessunits);
  const hasCostType = docType === 'invoice';
  const [expanded, setExpanded] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);

  const costTypeOptions = useMemo(
    () => [
      { label: 'Cost type…', value: '' },
      ...[...purchasetypes].sort((a, b) => String(a.BestellingType).localeCompare(String(b.BestellingType))).map((pt) => ({ label: pt.BestellingType, value: String(pt.BestellingTypeID) }))
    ],
    [purchasetypes]
  );
  const businessUnitOptions = useMemo(
    () => [
      { label: 'BU…', value: '' },
      ...businessunits
        .filter((bu) => bu.IsActive)
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((bu) => ({ label: bu.Code, value: String(bu.BusinessUnitID) }))
    ],
    [businessunits]
  );

  const { lines, target, allocated, remaining, isBalanced, rowErrors } = draft;
  const editableRows = lines.filter((row) => row.editable);
  const compact = compactAllowed && !expanded && lines.length === 1 && editableRows.length === 1 && target !== null;
  const poProjects = purchaseOrders.filter((po) => po.ProjectID);
  const projectSuggestion = poProjects[0] ? { id: poProjects[0].ProjectID, number: poProjects[0].projectNumber } : prefill?.projectId ? { id: prefill.projectId, number: prefill.projectNumber } : null;

  const renderReadOnly = (row, index) => (
    <div key={row._key} style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS(hasCostType), gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #ebebeb', opacity: 0.85 }}>
      <Text fontWeight="semibold">{formatToCurrency(row.Bedrag)}</Text>
      {hasCostType ? <Text>{purchasetypes.find((pt) => pt.BestellingTypeID === row.FactuurTypeID)?.BestellingType || '-'}</Text> : null}
      <Text>{row.projectNumber ? `🚧 ${row.projectNumber} ${row.projectDescription || ''}` : '-'}</Text>
      <Text>{businessunits.find((bu) => bu.BusinessUnitID === row.BusinessUnitID)?.Code || '-'}</Text>
      <Text tone="subdued">{row.Notitie || ''}</Text>
      <Tooltip content={row.RejectReason ? `Rejected: ${row.RejectReason}` : `${labelFor(row.Status)} · ${row.approverAfkorting || '?'}`}>
        <Badge tone={toneFor(row.Status)}>{`${row.approverAfkorting || '?'} · ${labelFor(row.Status)}`}</Badge>
      </Tooltip>
      <span />
    </div>
  );

  const renderEditable = (row, index) => {
    const errors = rowErrors[index] || [];
    const handedOff = row.approverId && row.approverId !== row.originalApproverId;
    const menuItems = [
      ...(onHandOff ? [{ content: 'Hand off to colleague…', onAction: () => onHandOff(index) }] : []),
      { content: 'Fill to balance', onAction: () => draft.fillToBalance(index) },
      ...(lines.length > 1 ? [{ content: 'Remove line', destructive: true, onAction: () => draft.remove(index) }] : [])
    ];
    return (
      <div key={row._key} style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS(hasCostType), gap: 8, alignItems: 'start', padding: '6px 0', borderBottom: '1px solid #ebebeb' }}>
        <TextField
          id={index === 0 ? 'approvalValue' : `approvalValue-${index}`}
          label="Amount"
          labelHidden
          type="number"
          step={0.01}
          prefix="€"
          autoComplete="off"
          value={String(row.Bedrag ?? '')}
          onChange={(value) => draft.update(index, { Bedrag: value })}
          error={errors.find((e) => e.startsWith('Amount'))}
          disabled={compact}
        />
        {hasCostType ? (
          <Select
            id={index === 0 ? 'selectType' : `selectType-${index}`}
            label="Cost type"
            labelHidden
            options={costTypeOptions}
            value={row.FactuurTypeID ? String(row.FactuurTypeID) : ''}
            onChange={(value) => draft.update(index, { FactuurTypeID: value ? Number(value) : null })}
            error={errors.find((e) => e.startsWith('Cost type'))}
          />
        ) : null}
        <ProjectSelect value={row.ProjectID} id="ProjectID" rowIndex={index} index={row._key} onChange={(value) => draft.update(index, { ProjectID: value || null })} />
        <Select
          label="Business unit"
          labelHidden
          options={businessUnitOptions}
          value={row.BusinessUnitID ? String(row.BusinessUnitID) : ''}
          onChange={(value) => draft.update(index, { BusinessUnitID: value ? Number(value) : null })}
        />
        <TextField label="Note" labelHidden placeholder="Note" autoComplete="off" value={row.Notitie || ''} onChange={(value) => draft.update(index, { Notitie: value })} />
        <div style={{ paddingTop: 6 }}>
          {handedOff ? (
            <Tooltip content="Will be handed to this colleague when you save or approve">
              <Badge tone="info">{`→ ${row.approverAfkorting || row.approverId}`}</Badge>
            </Tooltip>
          ) : row.id ? (
            <Badge tone={toneFor(row.Status)}>{row.approverAfkorting ? `${row.approverAfkorting} · ${labelFor(row.Status)}` : labelFor(row.Status)}</Badge>
          ) : (
            <Badge>new</Badge>
          )}
        </div>
        <Popover
          active={openMenu === row._key}
          onClose={() => setOpenMenu(null)}
          activator={<Button icon={MenuHorizontalIcon} variant="tertiary" accessibilityLabel="Line actions" onClick={() => setOpenMenu(openMenu === row._key ? null : row._key)} />}
        >
          <ActionList
            items={menuItems.map((item) => ({
              ...item,
              onAction: () => {
                setOpenMenu(null);
                item.onAction();
              }
            }))}
          />
        </Popover>
      </div>
    );
  };

  return (
    <BlockStack gap="200">
      <div style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS(hasCostType), gap: 8, padding: '0 0 4px 0' }}>
        {['Amount', ...(hasCostType ? ['Cost type'] : []), 'Project', 'BU', 'Note', 'Approver', ''].map((heading) => (
          <Text key={heading || 'actions'} variant="bodySm" tone="subdued">
            {heading}
          </Text>
        ))}
      </div>
      {lines.map((row, index) => (row.editable && canEdit ? renderEditable(row, index) : renderReadOnly(row, index)))}
      {lines.length === 0 ? (
        <Box padding="200">
          <Text tone="subdued">No allocation lines yet.</Text>
        </Box>
      ) : null}

      <InlineStack align="space-between" blockAlign="center" wrap>
        <InlineStack gap="200" blockAlign="center">
          {target !== null ? (
            <>
              <Text variant="bodySm" tone="subdued">
                Allocated {formatToCurrency(allocated)} of {formatToCurrency(target)}
              </Text>
              <Badge tone={isBalanced ? 'success' : 'critical'}>{isBalanced ? 'Balanced' : `Remaining ${formatToCurrency(remaining)}`}</Badge>
            </>
          ) : (
            <Text variant="bodySm" tone="subdued">
              No document amount known yet
            </Text>
          )}
        </InlineStack>
        {canEdit ? (
          <ButtonGroup>
            {compact ? (
              <Button
                onClick={() => {
                  setExpanded(true);
                  draft.add({ Bedrag: 0 });
                }}
              >
                Split
              </Button>
            ) : (
              <>
                <Button id="addApproval" icon={PlusIcon} onClick={() => draft.add({ Bedrag: 0 })}>
                  Add line
                </Button>
                <Button onClick={draft.splitRemaining} disabled={target === null || remaining <= 0}>
                  Split remaining
                </Button>
                <Button onClick={draft.splitEvenly} disabled={target === null || editableRows.length < 2}>
                  Split evenly
                </Button>
              </>
            )}
            {projectSuggestion ? (
              <Button onClick={() => draft.applyProject(projectSuggestion.id, projectSuggestion.number)} disabled={!editableRows.some((row) => !row.ProjectID)}>
                Use PO project {projectSuggestion.number}
              </Button>
            ) : null}
          </ButtonGroup>
        ) : null}
      </InlineStack>
    </BlockStack>
  );
}
